const COMMONS_API_BASE = 'https://commons.wikimedia.org/w/api.php';
const API_USER_AGENT = 'CurriculumBuilder/1.0 (Educational curriculum tool; https://github.com)';

export interface WikimediaImage {
  pageId: number;
  title: string;
  thumbUrl: string;
  fullUrl: string;
  descriptionUrl: string;
  width: number | null;
  height: number | null;
  mime: string | null;
  license: string | null;
  artist: string | null;
}

interface WikimediaApiResponse {
  continue?: { gsroffset?: number };
  query?: {
    pages?: Record<string, WikimediaPage>;
  };
}

interface WikimediaPage {
  pageid: number;
  title: string;
  imageinfo?: WikimediaImageInfo[];
}

interface WikimediaImageInfo {
  url?: string;
  thumburl?: string;
  descriptionurl?: string;
  size?: number;
  width?: number;
  height?: number;
  mime?: string;
  extmetadata?: Record<string, { value?: string | number }>;
}

export async function searchWikimediaImages(query: string, limit = 20): Promise<WikimediaImage[]> {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return [];

  const url = new URL(COMMONS_API_BASE);
  url.searchParams.set('action', 'query');
  url.searchParams.set('generator', 'search');
  url.searchParams.set('gsrsearch', `${trimmedQuery} filetype:bitmap|drawing`);
  url.searchParams.set('gsrnamespace', '6');
  url.searchParams.set('gsrlimit', String(limit));
  url.searchParams.set('prop', 'imageinfo');
  url.searchParams.set('iiprop', 'url|size|mime|extmetadata');
  url.searchParams.set('iiurlwidth', '330');
  url.searchParams.set('format', 'json');
  url.searchParams.set('origin', '*');

  const response = await fetch(url.toString(), {
    headers: {
      'Api-User-Agent': API_USER_AGENT,
      'Accept-Encoding': 'gzip',
    },
  });

  if (!response.ok) {
    if (response.status === 429) {
      throw new Error('Wikimedia rate limit reached. Please wait a moment and try again.');
    }
    throw new Error(`Wikimedia Commons search failed (${response.status}).`);
  }

  const data = (await response.json()) as WikimediaApiResponse;
  const pages = data.query?.pages;
  if (!pages) return [];

  return Object.values(pages)
    .map((page): WikimediaImage | null => {
      const info = page.imageinfo?.[0];
      if (!info) return null;

      const thumbUrl = info.thumburl || '';
      const fullUrl = info.url || '';
      if (!thumbUrl && !fullUrl) return null;

      const ext = info.extmetadata || {};
      const rawArtist = readExtMeta(ext, 'Artist');
      const artist = rawArtist ? stripHtml(rawArtist) : null;

      return {
        pageId: page.pageid,
        title: cleanTitle(page.title),
        thumbUrl,
        fullUrl,
        descriptionUrl: info.descriptionurl || '',
        width: typeof info.width === 'number' ? info.width : null,
        height: typeof info.height === 'number' ? info.height : null,
        mime: typeof info.mime === 'string' ? info.mime : null,
        license: readExtMeta(ext, 'LicenseShortName'),
        artist,
      };
    })
    .filter((img): img is WikimediaImage => Boolean(img));
}

export function getWikimediaThumbnailUrl(image: WikimediaImage): string {
  return image.thumbUrl || image.fullUrl;
}

export function getWikimediaImageUrl(image: WikimediaImage): string {
  return image.fullUrl || image.thumbUrl;
}

export function generateWikimediaAltText(image: WikimediaImage, searchTerm?: string): string {
  const title = image.title?.trim();
  if (title) return title;
  const fallback = searchTerm?.trim();
  return fallback ? `Image of ${fallback}` : 'Image from Wikimedia Commons';
}

export function buildWikimediaCredit(image: WikimediaImage): string {
  const creator = image.artist?.trim() || 'Unknown creator';
  const license = image.license || 'CC';
  return `"${image.title?.trim() || 'Untitled'}" by ${creator} (${license}) via Wikimedia Commons`;
}

function readExtMeta(ext: Record<string, { value?: string | number }>, key: string): string | null {
  const entry = ext[key];
  if (!entry) return null;
  const value =
    typeof entry.value === 'string'
      ? entry.value.trim()
      : typeof entry.value === 'number'
        ? String(entry.value)
        : null;
  return value && value.length > 0 ? value : null;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

function cleanTitle(title: string): string {
  // Remove "File:" prefix and file extension
  return title
    .replace(/^File:/, '')
    .replace(/\.[^.]+$/, '')
    .replace(/_/g, ' ')
    .trim();
}
